import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Returns today's date as YYYY-MM-DD in UK local time (Europe/London).
 * Prevents UTC drift where e.g. 11pm BST on 8 Mar shows as 9 Mar in UTC.
 */
export function getUKDateString(date?: Date): string {
  const d = date ?? new Date();
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const y = parts.find(p => p.type === "year")!.value;
  const m = parts.find(p => p.type === "month")!.value;
  const day = parts.find(p => p.type === "day")!.value;
  return `${y}-${m}-${day}`;
}

/**
 * Returns the UTC instants for the start and end of a UK calendar day,
 * handling GMT/BST automatically. Use for "today" range queries so a
 * device set to another timezone still sees the UK working day.
 */
export function getUKDayRange(date?: Date): { start: string; end: string } {
  const day = getUKDateString(date);
  const offsetLabel = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London",
    timeZoneName: "shortOffset",
  })
    .formatToParts(date ?? new Date())
    .find((p) => p.type === "timeZoneName")?.value ?? "GMT+0";
  const offsetHours = parseInt(offsetLabel.replace("GMT", "") || "0", 10) || 0;
  const sign = offsetHours < 0 ? "-" : "+";
  const pad = String(Math.abs(offsetHours)).padStart(2, "0");
  const start = new Date(`${day}T00:00:00${sign}${pad}:00`);
  const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
  return { start: start.toISOString(), end: end.toISOString() };
}
